// Derives bit depth and chroma subsampling from RFC 6381 codec strings (avc1/hvc1/hev1/vp09/av01).

export interface CodecTraits {
  family: 'h264' | 'hevc' | 'vp9' | 'av1' | 'prores' | 'other';
  bitDepth: 8 | 10 | 12;
  chroma: '4:2:0' | '4:2:2' | '4:4:4';
  profileName?: string;
}

export function codecTraits(codec: string): CodecTraits {
  const c = codec.trim();
  const lower = c.toLowerCase();
  if (lower.startsWith('avc1') || lower.startsWith('avc3')) {
    const profile = parseInt(c.slice(5, 7), 16);
    // 100 High, 110 High 10, 122 High 4:2:2, 244 High 4:4:4 Predictive
    if (profile === 110) return { family: 'h264', bitDepth: 10, chroma: '4:2:0', profileName: 'High 10' };
    if (profile === 122) return { family: 'h264', bitDepth: 10, chroma: '4:2:2', profileName: 'High 4:2:2' };
    if (profile === 244) return { family: 'h264', bitDepth: 10, chroma: '4:4:4', profileName: 'High 4:4:4' };
    const names: Record<number, string> = { 66: 'Baseline', 77: 'Main', 88: 'Extended', 100: 'High' };
    return { family: 'h264', bitDepth: 8, chroma: '4:2:0', profileName: names[profile] };
  }
  if (lower.startsWith('hvc1') || lower.startsWith('hev1')) {
    // hvc1.<profile_space><profile_idc>.<compat>.<tier><level>.<constraints>
    const parts = c.split('.');
    const prof = parseInt((parts[1] ?? '1').replace(/^[A-Ca-c]/, ''), 10);
    if (prof === 2) return { family: 'hevc', bitDepth: 10, chroma: '4:2:0', profileName: 'Main 10' };
    if (prof === 4) return { family: 'hevc', bitDepth: 10, chroma: '4:2:2', profileName: 'RExt' };
    return { family: 'hevc', bitDepth: 8, chroma: '4:2:0', profileName: prof === 1 ? 'Main' : `Profile ${prof}` };
  }
  if (lower.startsWith('vp09')) {
    const parts = c.split('.');
    const depth = parseInt(parts[3] ?? '8', 10);
    const cs = parseInt(parts[4] ?? '1', 10);
    return {
      family: 'vp9',
      bitDepth: depth >= 12 ? 12 : depth >= 10 ? 10 : 8,
      chroma: cs === 2 ? '4:2:2' : cs === 3 ? '4:4:4' : '4:2:0',
    };
  }
  if (lower.startsWith('av01')) {
    const parts = c.split('.');
    const depth = parseInt(parts[3] ?? '8', 10);
    return { family: 'av1', bitDepth: depth >= 12 ? 12 : depth >= 10 ? 10 : 8, chroma: '4:2:0' };
  }
  if (lower.startsWith('apch') || lower.startsWith('apcn') || lower.startsWith('ap4h') || lower.includes('prores'))
    return { family: 'prores', bitDepth: 10, chroma: '4:2:2' };
  return { family: 'other', bitDepth: 8, chroma: '4:2:0' };
}

/** Bit depth implied by a VideoFrame pixel format. */
export function formatBitDepth(format: string | null): 8 | 10 | 12 | null {
  if (!format) return null;
  if (format.includes('P10')) return 10;
  if (format.includes('P12')) return 12;
  return 8;
}

export function formatChroma(format: string | null): '4:2:0' | '4:2:2' | '4:4:4' | null {
  if (!format) return null;
  if (format.startsWith('I422')) return '4:2:2';
  if (format.startsWith('I444') || format.startsWith('RGB') || format.startsWith('BGR')) return '4:4:4';
  return '4:2:0';
}
